import React, { useState } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Stack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Flex } from "@UI/Components/Flex/Flex";
import { IImproveCard, ImproveCard } from "./ImproveCard";
import { SuperTalentGoalCard } from "./SuperTalentGoalCard";
import { QuestionModal } from "./QuestionModal";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import messages from "./messages";
import { useRealisationRates } from "@modules/ServiceProviders/hooks/useRealisationRate";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";

interface IImproveAndGoal {
  context: "repeat_rate" | "realisation_rate" | "realisation_rate_blocked" | "selection_rate";
}

export const ImproveAndGoal = ({ context }: IImproveAndGoal) => {
  const [type, setType] = useState("");
  const { isMobile } = useMediaQuery();
  const { generateRoute } = useRoutes();

  const { currentUser } = useCurrentUser({
    includes: ["serviceProvider"],
  });

  const { data: realisationRateMetrics, isLoading } = useRealisationRates(
    context === "realisation_rate" ? currentUser?.serviceProvider?.id : undefined
  );

  let questionContext = context;

  const isBlocked = realisationRateMetrics?.metric?.currentLevel === "blocked";
  if (context === "realisation_rate" && isBlocked) {
    questionContext = "realisation_rate_blocked";
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  const checkContext = {
    repeat_rate: ["share_profile", "respect_rules", "how_to_super"],
    realisation_rate: ["do_jobs", "ask_questions", "discover_advice"],
    realisation_rate_blocked: [
      "finish_jobs",
      "current_jobs_questions",
      "work_previous_clients",
    ],
    selection_rate: ["quality_offers", "follow_feedback", "be_patient"],
  };

  const handleOpenCard = (type: string) => {
    setType(type);
  };

  const profileUrl = currentUser?.serviceProvider?.id
    ? generateRoute(["SERVICE_PROVIDER_PROFILE"], { id: String(currentUser.serviceProvider.id) })
    : undefined;

  return (
    <Stack direction={["column", "row"]} mt={{ base: 2, md: 3 }} spacing={0}>
      <Flex
        flexDirection="column"
        w="full"
        pr={{ base: 0, md: 8 }}
        pb={{ base: 5, md: 0 }}
        borderRightWidth={{ base: 0, md: "1px" }}
        borderRightColor={{ base: "none", md: "gray.300" }}
        borderBottomWidth={{ base: "1px", md: 0 }}
        borderBottomColor={{ base: "gray.300", md: "none" }}
      >
        <Heading textAlign="start" fontSize={{ base: "xl", lg: "3xl" }}>
          <FormattedMessage {...messages.improveScore} />
        </Heading>
        <Stack
          direction="column"
          mt={{ base: 3, md: 3 }}
          spacing={{ base: 2, md: 3 }}
        >
          {checkContext[questionContext]?.map((element, index) => (
            <ImproveCard
              key={index}
              type={element as IImproveCard["type"]}
              onClick={() => handleOpenCard(element)}
            />
          ))}
        </Stack>
      </Flex>
      <Flex w="full" pl={{ base: 0, md: 8 }} pb={{ base: 5, md: 0 }}>
        <SuperTalentGoalCard context={context} />
      </Flex>
      <QuestionModal
        isMobile={isMobile}
        isOpen={!!type}
        onClose={() => setType("")}
        profileUrl={profileUrl}
        type={type}
      />
    </Stack>
  );
};
